import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getApplicantInformation} from 'src/action-creators/crm';
import {
  selectApplicantInformation,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {useSelector, useDispatch} from 'react-redux';
import {useInflight} from 'src/hooks/redux';

import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import CandidateResponseForm from 'src/components/crm/applicants/body/tabComponents/forms/candidate-response-form';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import InternalResponseForm from 'src/components/crm/applicants/body/tabComponents/forms/internal-response-form';
import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import * as reduxApi from 'src/utils/redux-api-v2';

import css from './candidate-forms-submitted.css';


const CandidateFormsSubmitted = ({
  jobId,
  candidateId,
  isPublicLink,
  reloadCandidateData,
  showActionButtons = true,
}: {
  jobId: number;
  candidateId: number;
  isPublicLink?: boolean;
  reloadCandidateData: () => void;
  showActionButtons?: boolean;
}) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const userInfo = useSelector(selectUserInfo);
  const viewCandidateForm = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_CANDIDATE_FORM,
  );
  const applicantDetails = useSelector(selectApplicantInformation);
  const isApplicantsLoading = useInflight(
    getApplicantInformation(candidateId, jobId.toString()),
  );

  const [loadingPreviousJobForm, setLoadingPreviousJobForm] =
    React.useState(false);
  const [previousJobForm, setPreviousJobForm] = React.useState<any>(null);

  const currentJobApplication = applicantDetails?.applications?.find(
    (application: any) =>
      application.jobId === jobId &&
      !application.duplicate &&
      application.jobId > 0 &&
      application.active,
  );

  React.useEffect(() => {
    if (!applicantDetails && Boolean(candidateId) && Boolean(jobId)) {
      dispatch(getApplicantInformation(candidateId, jobId.toString()));
    }
  }, []);

  React.useEffect(() => {
    if (!jobId || !applicantDetails?.id) {
      return;
    }

    const fetchApplicantPreviousForm = async () => {
      setLoadingPreviousJobForm(true);
      await dispatch(
        reduxApi.get(
          `api/relogged-custom-form?jobId=${jobId}&candidateId=${applicantDetails?.id}`,
          {},
          {apiPath: '/trmapi/'},
        ),
      )
        .then((data: any) => setPreviousJobForm(data))
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          setLoadingPreviousJobForm(false);
        });
    };

    fetchApplicantPreviousForm();
  }, [jobId, applicantDetails?.id]);

  if (isApplicantsLoading) {
    return <CircularLoader />;
  }

  if (!currentJobApplication) {
    return null;
  }

  const {stepCandidates} = currentJobApplication;

  const formInterviews: Array<{
    submittedForm: any;
    type: string;
  }> = [];
  stepCandidates.forEach((sc) => {
    // @ts-expect-error - TS2339 - Property 'interviewCandidateFormField' does not exist on type 'ApplicationStepCandidate'.
    if (sc.interviewCandidateFormField) {
      formInterviews.push({
        type: 'CANDIDATE_FORM',
        submittedForm: sc,
      });
      // @ts-expect-error - TS2339 - Property 'jobInternalForm' does not exist on type 'ApplicationStepCandidate'.
    } else if (sc.jobInternalForm) {
      formInterviews.push({
        type: 'INTERNAL_FORM',
        submittedForm: sc,
      });
    }
  });

  return (
    <div className={css.formContainer}>
      <CollapsibleCard
        classNames={{wrapper: css.formsCard}}
        title={t('From_Current_job', 'From Current job')}
        isOpen={true}
      >
        <CollapsibleCardContent className={css.formsCardContentContainer}>
          {formInterviews.length > 0 ? (
            // @ts-expect-error - TS7030 - Not all code paths return a value.
            formInterviews.map((formInterview) => {
              if (
                formInterview.type === 'CANDIDATE_FORM' &&
                viewCandidateForm
              ) {
                return (
                  <>
                    <CandidateResponseForm
                      formInterview={formInterview.submittedForm}
                      applicationId={currentJobApplication.id}
                      isPublicLink={isPublicLink}
                      candidateName={applicantDetails.name ?? ''}
                      reloadCandidateData={reloadCandidateData}
                      showActionButtons={showActionButtons}
                    />
                  </>
                );
              }
              if (formInterview.type === 'INTERNAL_FORM') {
                return (
                  <>
                    <InternalResponseForm
                      formInterview={formInterview.submittedForm}
                      applicationId={currentJobApplication.id}
                      isPublicLink={isPublicLink}
                      candidateName={applicantDetails.name ?? ''}
                    />
                  </>
                );
              }
            })
          ) : (
            <div className={css.emptyStateContainer}>
              <EmptyState
                title={t('No_Submissions_Yet', 'No Submissions Yet')}
                description={t(
                  'No_forms_have_been_submitted_yet',
                  'No forms have been submitted yet',
                )}
                imageVariant="data"
              />
            </div>
          )}
        </CollapsibleCardContent>
      </CollapsibleCard>
      <CollapsibleCard
        classNames={{wrapper: css.card}}
        title={t('From_Past_job', 'From Past job')}
        isOpen={false}
      >
        <CollapsibleCardContent className={css.cardContent}>
          {loadingPreviousJobForm ? (
            <CircularLoader />
          ) : previousJobForm ? (
            <iframe
              title="resume-iframe"
              className="doc"
              src={previousJobForm?.publicUrl}
              height="1000px"
              width="100%"
            />
          ) : (
            <BodyMedium>
              {t(
                'Previous_job\'s_form_is_not_available',
                'Previous job\'s form is not available',
              )}
            </BodyMedium>
          )}
        </CollapsibleCardContent>
      </CollapsibleCard>
    </div>
  );
};

export default CandidateFormsSubmitted;
